import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import React, { useState, useEffect, useRef } from "react";
import { Dropdown } from "primereact/dropdown";
import { Paginator } from "primereact/paginator";
import { useHistory } from "react-router";
import { BreadCrumb } from "primereact/breadcrumb";
import { Toast } from "primereact/toast";
import * as constants from "../app_utils/constants/Constants";
import {
  HOME_ROUTE_PATH,
} from "../app_utils/route_paths/resolver/PageRoutes";
import { PrimeIcons } from "primereact/api";
import { BaseApiServiceImpl } from "../app_utils/api/BaseApiServiceImpl";
import { MessageUtils } from "../app_utils/utils/MessageUtils";
import { paginatorTemplate } from "../app_utils/components/PaginatorTemplate";
import { generalStatusBodyTemplate } from "../app_utils/utils/Utils";

const STATUS_OPTIONS = [
  { label: "All Statuses", value: null },
  { label: "Received", value: "RECEIVED" },
  { label: "Processed", value: "PROCESSED" },
  { label: "Failed", value: "FAILED" },
  { label: "Dead Letter", value: "DEAD_LETTER" },
];

const PROVIDER_OPTIONS = [
  { label: "All Providers", value: null },
  { label: "KaCyber", value: "KACYBER" },
  { label: "Flutterwave", value: "FLUTTERWAVE" },
  { label: "Stripe", value: "STRIPE" },
  { label: "Pesapal", value: "PESAPAL" },
];

const CallbacksView = () => {
  const [records, setRecords] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchTermFilter, setSearchTermFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [providerFilter, setProviderFilter] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [first, setFirst] = useState<number>(0);
  const [limit, setLimit] = useState<number>(constants.MAXIMUM_RECORDS_PER_PAGE);
  const offsetRef = useRef<number>(0);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  const toast = useRef<Toast>(null);
  const history = useHistory();

  const breadcrumbHome = {
    icon: "pi pi-home",
    command: () => {
      history.push(HOME_ROUTE_PATH);
    },
  };

  const breadcrumbItems = [
    {
      label: "Payment Callbacks",
      icon: PrimeIcons.SYNC,
    },
  ];

  const getQueryParameters = () => {
    let searchParameters: any = { offset: offsetRef.current, limit: limit };
    if (searchTermFilter !== null) searchParameters.searchTerm = searchTermFilter;
    if (statusFilter !== null) searchParameters.status = statusFilter;
    if (providerFilter !== null) searchParameters.provider = providerFilter;
    return searchParameters;
  };

  const fetchRecordsFromServer = () => {
    setIsLoading(true);
    new BaseApiServiceImpl("/api/v1/admin/callbacks")
      .getRequestWithJsonResponse(getQueryParameters())
      .then(async (response) => {
        setIsLoading(false);
        setRecords(response?.records ?? []);
        setTotalItems(response?.totalItems ?? 0);
      })
      .catch((error) => {
        setIsLoading(false);
        MessageUtils.showErrorMessage(toast, error.message);
      });
  };

  useEffect(() => {
    fetchRecordsFromServer();
  }, []);

  const onSubmitFilter = () => {
    offsetRef.current = 0;
    setFirst(0);
    fetchRecordsFromServer();
  };

  const replayCallback = async (row: any) => {
    if (!row?.id) return;
    setIsRefreshing(true);
    try {
      await new BaseApiServiceImpl(`/api/v1/admin/callbacks/${row.id}/replay`).postRequestWithJsonResponse({});
      toast.current?.show({
        severity: "success",
        summary: "Callback Replayed",
        detail: `Callback ${row.id} has been queued for retry.`,
      });
      fetchRecordsFromServer();
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message,
      });
    }
    setIsRefreshing(false);
  };

  const actionBodyTemplate = (row: any) => (
    <Button
      icon="pi pi-refresh"
      className="p-button-text p-button-sm"
      tooltip="Replay Callback"
      loading={isRefreshing}
      onClick={() => replayCallback(row)}
      disabled={row?.status === "PROCESSED"}
    />
  );

  const onPage = (event: any) => {
    setLimit(event.rows);
    setFirst(event.first);
    offsetRef.current = event.first;
    fetchRecordsFromServer();
  };

  const statusBodyTemplate = (row: any) => {
    const status = (row?.status || "").toUpperCase();
    let className = "p-tag p-tag-warning";
    if (status === "PROCESSED") className = "p-tag p-tag-success";
    else if (status === "FAILED" || status === "DEAD_LETTER") className = "p-tag p-tag-danger";
    return <span className={className}>{row?.status || "-"}</span>;
  };

  return (
    <div className="card">
      <div className="p-d-flex p-jc-between">
        <BreadCrumb home={breadcrumbHome} model={breadcrumbItems} />
      </div>
      <div className="grid mt-3">
        <div className="col-12 md:col-3">
          <label htmlFor="search" className="w-full p-d-block">
            Search
          </label>
          <input
            id="search"
            type="text"
            className="form-control w-full"
            placeholder="Search by reference or idempotency key"
            value={searchTermFilter || ""}
            onChange={(e) => setSearchTermFilter(e.target.value)}
          />
        </div>
        <div className="col-12 md:col-3">
          <label htmlFor="provider" className="w-full p-d-block">
            Provider
          </label>
          <Dropdown
            id="provider"
            value={providerFilter}
            options={PROVIDER_OPTIONS}
            onChange={(e) => setProviderFilter(e.value)}
            placeholder="Select provider"
            className="w-full"
          />
        </div>
        <div className="col-12 md:col-3">
          <label htmlFor="status" className="w-full p-d-block">
            Status
          </label>
          <Dropdown
            id="status"
            value={statusFilter}
            options={STATUS_OPTIONS}
            onChange={(e) => setStatusFilter(e.value)}
            placeholder="Select status"
            className="w-full"
          />
        </div>
        <div className="col-12 md:col-2 mt-4">
          <Button
            label="Filter"
            icon="pi pi-filter"
            className="p-button-primary mr-1"
            onClick={onSubmitFilter}
          />
        </div>
      </div>

      <DataTable
        value={records || []}
        loading={isLoading}
        responsiveLayout="scroll"
        emptyMessage="No callbacks found."
        className="mt-3"
      >
        <Column field="id" header="ID" style={{ width: "70px" }} />
        <Column field="provider" header="Provider" />
        <Column field="gatewayId" header="Gateway ID" />
        <Column field="transactionReference" header="Transaction Ref" />
        <Column field="idempotencyKey" header="Idempotency Key" />
        <Column field="status" header="Status" body={statusBodyTemplate} />
        <Column field="attempts" header="Attempts" />
        <Column field="errorMessage" header="Error" />
        <Column field="dateCreated" header="Date" />
        <Column body={actionBodyTemplate} style={{ width: "4rem" }} />
      </DataTable>

      <Paginator
        first={first}
        rows={limit}
        totalRecords={totalItems}
        alwaysShow={true}
        onPageChange={onPage}
        template={paginatorTemplate}
      />

      <Toast ref={toast} />
    </div>
  );
};

export default CallbacksView;
